import {
  FileTextOutlined,
  PictureOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { getAccessToken } from "../../../utils/auth0Utils";

export const IMAGE_FILE_EXTENSIONS = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
export const TEXT_FILE_EXTENSIONS = /\.(txt|log|md|json|xml|html|css|js|jsx|ts|tsx)$/i;

export const ZOOM_LIMITS = {
  MIN: 0.5,
  MAX: 5,
  STEP: 0.25,
  WHEEL_STEP: 0.1,
};

/**
 * Detects the file type based on file extension and MIME type
 * @param {Object} file - File object with fileName and fileType
 * @returns {Object} File type information
 */
export const detectFileType = (file) => {
  if (!file) {
    return { type: null, isImage: false, isPdf: false, isText: false };
  }

  const mimeType = (file?.fileType || "").toLowerCase();
  const fileName = (file?.fileName || "").toLowerCase();

  const isImage = mimeType.includes("image") || IMAGE_FILE_EXTENSIONS.test(fileName);
  const isPdf = mimeType.includes("pdf") || fileName.endsWith(".pdf");
  const isText = mimeType.includes("text") || TEXT_FILE_EXTENSIONS.test(fileName);

  return {
    type: isImage ? "image" : isPdf ? "pdf" : isText ? "text" : "other",
    isImage,
    isPdf,
    isText,
  };
};

/**
 * Gets the appropriate icon for a file type
 * @param {string} fileType - Type of file (image, pdf, text, other)
 * @returns {ReactNode} Icon component
 */
export const getFileTypeIcon = (fileType) => {
  if (fileType === "image") return <PictureOutlined />;
  if (fileType === "pdf") return <FilePdfOutlined />;
  return <FileTextOutlined />;
};

/**
 * Adds view=true parameter to PDF URL for inline display
 * @param {string} url - Original PDF URL
 * @returns {string} URL with view parameter
 */
export const addViewParameterToUrl = (url) => {
  try {
    const urlObject = new URL(url);
    urlObject.searchParams.set("view", "true");
    return urlObject.toString();
  } catch {
    return `${url}${url.includes("?") ? "&" : "?"}view=true`;
  }
};

/**
 * Fetches a PDF file as a blob for inline display
 * @param {string} pdfUrl - URL of the PDF file
 * @returns {Promise<Blob>} PDF blob
 */
export const fetchPdfAsBlob = async (pdfUrl) => {
  const url = addViewParameterToUrl(pdfUrl);
  const token = await getAccessToken().catch(() => null);

  const headers = {
    Accept: "application/pdf",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
  }

  const pdfBlob = await response.blob();
  if (pdfBlob.size === 0) {
    throw new Error("Empty PDF blob received");
  }

  return pdfBlob;
};

/**
 * Fetches an image file as a blob for inline display
 * @param {string} imageUrl - URL of the image file
 * @returns {Promise<Blob>} Image blob
 */
export const fetchImageAsBlob = async (imageUrl) => {
  const token = await getAccessToken().catch(() => null);

  const headers = {
    Accept: "image/*",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const response = await fetch(imageUrl, {
    method: "GET",
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
  }

  const imageBlob = await response.blob();
  if (imageBlob.size === 0) {
    throw new Error("Empty image blob received");
  }

  return imageBlob;
};

